import { useState } from 'react';
import { Equipment, EquipmentSector } from '@haccp/api-client';
import {
  validateEquipmentForm,
  EQUIPMENT_TYPES,
  EquipmentFormData,
  isFryerType,
} from '@/utils/equipment';

interface EquipmentFormProps {
  equipment?: Equipment;
  sectors?: EquipmentSector[];
  onSubmit: (data: {
    name: string;
    type: string;
    minTempC: number;
    maxTempC: number;
    sectorId?: string;
  }) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  isFryer?: boolean;
}

export function EquipmentForm({
  equipment,
  sectors,
  onSubmit,
  onCancel,
  isLoading = false,
  isFryer = false,
}: EquipmentFormProps) {
  const [formData, setFormData] = useState<EquipmentFormData>({
    name: equipment?.name || '',
    type: isFryer ? 'Friteuse' : (equipment?.type || ''),
    minTempC: equipment?.minTempC?.toString() || '',
    maxTempC: equipment?.maxTempC?.toString() || '',
  });
  const [sectorId, setSectorId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (
    field: keyof EquipmentFormData,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const validationErrors = validateEquipmentForm(formData);
    const errorMap: Record<string, string> = validationErrors.reduce(
      (acc, err) => ({ ...acc, [err.field]: err.message }),
      {}
    );

    if (!equipment && sectors && sectors.length > 0 && sectorId === null) {
      errorMap.sectorId = 'Veuillez sélectionner un secteur';
    }

    if (Object.keys(errorMap).length > 0) {
      setErrors(errorMap);
      return;
    }

    const isCurrentFryer = isFryerType(formData.type);

    try {
      await onSubmit({
        name: formData.name.trim(),
        type: formData.type.trim(),
        minTempC: isCurrentFryer ? 0 : parseFloat(formData.minTempC),
        maxTempC: isCurrentFryer ? 0 : parseFloat(formData.maxTempC),
        ...(sectorId !== null ? { sectorId } : {}),
      });
    } catch (err: any) {
      setErrors({
        submit: err.response?.data?.message || 'Échec de l\'enregistrement',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {!equipment && sectors && sectors.length > 0 && (
        <div>
          <label className="block text-sm font-semibold text-neutral-900 mb-2">
            Secteur <span className="text-error">*</span>
          </label>
          <select
            value={sectorId ?? ''}
            onChange={(e) => setSectorId(e.target.value || null)}
            disabled={isLoading}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
              errors.sectorId
                ? 'border-error focus:ring-error'
                : 'border-neutral-300 focus:ring-primary-500'
            }`}
          >
            <option value="">Sélectionner un secteur...</option>
            {sectors.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          {errors.sectorId && <p className="mt-1 text-sm text-error">{errors.sectorId}</p>}
        </div>
      )}
      <div>
        <label className="block text-sm font-semibold text-neutral-900 mb-2">
          Nom de l'équipement
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          placeholder="ex: Réfrigérateur principal, Congélateur"
          disabled={isLoading}
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
            errors.name
              ? 'border-error focus:ring-error'
              : 'border-neutral-300 focus:ring-primary-500'
          }`}
        />
        {errors.name && <p className="mt-1 text-sm text-error">{errors.name}</p>}
      </div>

      <div>
        <label className="block text-sm font-semibold text-neutral-900 mb-2">
          Type d'équipement
        </label>
        <select
          value={formData.type}
          onChange={(e) => handleChange('type', e.target.value)}
          disabled={isLoading}
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
            errors.type
              ? 'border-error focus:ring-error'
              : 'border-neutral-300 focus:ring-primary-500'
          }`}
        >
          <option value="">Sélectionner un type...</option>
          {EQUIPMENT_TYPES.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
        {errors.type && <p className="mt-1 text-sm text-error">{errors.type}</p>}
      </div>

      {!isFryerType(formData.type) && (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-neutral-900 mb-2">
            Temp. min (°C)
          </label>
          <input
            type="number"
            step="0.1"
            value={formData.minTempC}
            onChange={(e) => handleChange('minTempC', e.target.value)}
            placeholder="ex: 2"
            disabled={isLoading}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
              errors.minTempC
                ? 'border-error focus:ring-error'
                : 'border-neutral-300 focus:ring-primary-500'
            }`}
          />
          {errors.minTempC && <p className="mt-1 text-sm text-error">{errors.minTempC}</p>}
        </div>

        <div>
          <label className="block text-sm font-semibold text-neutral-900 mb-2">
            Temp. max (°C)
          </label>
          <input
            type="number"
            step="0.1"
            value={formData.maxTempC}
            onChange={(e) => handleChange('maxTempC', e.target.value)}
            placeholder="ex: 4"
            disabled={isLoading}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
              errors.maxTempC
                ? 'border-error focus:ring-error'
                : 'border-neutral-300 focus:ring-primary-500'
            }`}
          />
          {errors.maxTempC && <p className="mt-1 text-sm text-error">{errors.maxTempC}</p>}
        </div>
      </div>
      )}

      {errors.submit && (
        <div className="p-3 bg-error/10 border border-error text-error rounded-lg text-sm">
          {errors.submit}
        </div>
      )}

      <div className="flex gap-3 pt-4">
        <button
          type="submit"
          disabled={isLoading}
          className="flex-1 px-4 py-2 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? 'Enregistrement...' : equipment ? 'Mettre à jour' : 'Créer l\'équipement'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="flex-1 px-4 py-2 bg-neutral-200 text-neutral-900 font-semibold rounded-lg hover:bg-neutral-300 disabled:opacity-50 transition-colors"
        >
          Annuler
        </button>
      </div>
    </form>
  );
}
